import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import DoubleTranslatedCustomRichText from '@/components/CustomRichText/DoubleTranslatedCustomRichText';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import InfoMsg from '@/components/UI/InfoMsg/InfoMsg';
import Spinner from '@/components/UI/Spinner/Spinner';
import * as Constants from '@/helpers/constants';
import { getEditorId, removeParagraphTags, tabLowerCase } from '@/helpers/utils';
import useToastContext from '@/hooks/useToastContext';
import { setHelpFeedback } from '@/redux/Help/help.action';
import { getHelpFeedback } from '@/redux/Help/help.reducer';
import administrationServices from '@/services/administrationServices';
import { ReduxHelp } from '@/types';
import styles from '../HelpText.module.scss';

type FeedbackHelpTextProps = {
  helpFeedback: ReduxHelp;
  setFeedback: (data: ReduxHelp) => void;
};

const FeedbackHelpText: React.FC<FeedbackHelpTextProps> = (props) => {
  const { t } = useTranslation();
  const tab = tabLowerCase(window.location.pathname.split('/')[4]);
  const [originalValues, setOriginalValues] = useState({});
  const { helpFeedback, setFeedback } = props;
  const isLoading = helpFeedback && !helpFeedback.FEEDBACK_TAB;
  const { addToast } = useToastContext();
  const FEEDBACK_TAB = 'FEEDBACK_TAB';
  const FEEDBACK_MANAGER_ENTRY = 'FEEDBACK_MANAGER_ENTRY';
  const FEEDBACK_APPROVER = 'FEEDBACK_APPROVER';
  const FEEDBACK_APPROVER_COMMENT_VANS = 'FEEDBACK_APPROVER_COMMENT_VANS';

  const ID_GENERAL_HARDWARE = 'general-hardware';
  const ID_REQUIREMENTS_HARDWARE = 'requirements-hardware';
  const ID_PARTS_AVAILABILITY = 'parts-availability';
  const ID_FEEDBACK_COMMENTS_VANS = 'feedback-comments-vans';

  useEffect(() => {
    administrationServices.getHelp(tab)
      .then((data) => {
        const values = helpFeedback && helpFeedback.modified ? helpFeedback : data;
        setFeedback(values);
        setOriginalValues(values);
      })
      .catch((error) => addToast(error.message, 'ERROR'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleWysiwygChange = (lang, label, data) => {
    const propValues = { ...helpFeedback[label] };
    const isModified = removeParagraphTags(data) !== removeParagraphTags(originalValues[label][lang]);
    const newState = {
      ...helpFeedback,
      [label]: {
        ...propValues,
        [lang]: data,
      },
      modified: isModified,
    };

    if (isModified) {
      setFeedback(newState as unknown as ReduxHelp);
      setOriginalValues(newState);
    }
  };

  return (
    <>
      {isLoading && <Spinner isVisible isMask />}
      {!isLoading && (
        <>
          <InfoMsg msg={t('administration.helpText.info')} />
          <AccordionPanel title={t('administration.helpText.generalText')}>
            <DoubleTranslatedCustomRichText
              id={ID_GENERAL_HARDWARE}
              title={t('helptext')}
              initialValue={helpFeedback?.FEEDBACK_TAB}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, FEEDBACK_TAB, newValue)}
            />
          </AccordionPanel>
          <AccordionPanel title={`${t('campaignRequests.title')}: ${t('feedback.managerEntry')}`}>
            <DoubleTranslatedCustomRichText
              id={ID_REQUIREMENTS_HARDWARE}
              title={t('helptext')}
              initialValue={helpFeedback?.FEEDBACK_MANAGER_ENTRY}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, FEEDBACK_MANAGER_ENTRY, newValue)}
            />
          </AccordionPanel>
          <AccordionPanel title={`${t('campaignRequests.title')}: ${t('feedback.approverTitle')}`}>
            <DoubleTranslatedCustomRichText
              id={ID_PARTS_AVAILABILITY}
              title={t('helptext')}
              initialValue={helpFeedback?.FEEDBACK_APPROVER}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, FEEDBACK_APPROVER, newValue)}
            />
          </AccordionPanel>

          <AccordionPanel title={`${t('campaignRequests.title')}: ${t('feedback.comments')} (VANS)`}>
            <DoubleTranslatedCustomRichText
              id={ID_FEEDBACK_COMMENTS_VANS}
              title={t('helptext')}
              initialValue={helpFeedback?.FEEDBACK_APPROVER_COMMENT_VANS}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, FEEDBACK_APPROVER_COMMENT_VANS, newValue)}
            />
          </AccordionPanel>
        </>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  helpFeedback: getHelpFeedback(state),
});

const actionCreators = {
  setFeedback: setHelpFeedback,
};

export default connect(mapStateToProps, actionCreators)(FeedbackHelpText);
